import type {
  ExcalidrawTextElement,
  FontFamilyValues,
} from "@excalidraw/element/types";

import { FONT_FAMILY, FONT_FAMILY_FALLBACKS } from "./constants";

/**
 * Encapsulates font metrics with additional font metadata.
 * */
export interface FontMetadata {
  /** for head & hhea metrics read the woff2 with https://fontdrop.info/ */
  metrics: {
    /** head.unitsPerEm metric */
    unitsPerEm: number;
    /** hhea.ascender metric */
    ascender: number;
    /** hhea.descender metric */
    descender: number;
    /** harcoded unitless line-height, https://github.com/excalidraw/excalidraw/pull/6360#issuecomment-1477635971 */
    lineHeight: number;
  };
  /** flag to indicate a deprecated font */
  deprecated?: true;
  /**
   * whether this is a font that users can use (= shown in font picker)
   */
  private?: true;
  /** flag to indiccate a local-only font */
  local?: true;
  /** flag to indicate a fallback font */
  fallback?: true;
  /** grouping in the font picker (also drives the picker icon) */
  category?: "sans" | "serif" | "mono" | "display" | "handwriting";
}

export const FONT_METADATA: Record<number, FontMetadata> = {
  [FONT_FAMILY.Excalifont]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 886,
      descender: -374,
      lineHeight: 1.25,
    },
    category: "handwriting",
  },
  [FONT_FAMILY.Nunito]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 1011,
      descender: -353,
      lineHeight: 1.25,
    },
    category: "sans",
  },
  [FONT_FAMILY["Lilita One"]]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 923,
      descender: -220,
      lineHeight: 1.15,
    },
    category: "display",
  },
  [FONT_FAMILY["Comic Shanns"]]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 750,
      descender: -250,
      lineHeight: 1.25,
    },
    category: "mono",
  },
  [FONT_FAMILY.Virgil]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 886,
      descender: -374,
      lineHeight: 1.25,
    },
    deprecated: true,
  },
  [FONT_FAMILY.Helvetica]: {
    metrics: {
      unitsPerEm: 2048,
      ascender: 1577,
      descender: -471,
      lineHeight: 1.15,
    },
    deprecated: true,
    local: true,
  },
  [FONT_FAMILY.Cascadia]: {
    metrics: {
      unitsPerEm: 2048,
      ascender: 1900,
      descender: -480,
      lineHeight: 1.2,
    },
    deprecated: true,
  },
  [FONT_FAMILY["Liberation Sans"]]: {
    metrics: {
      unitsPerEm: 2048,
      ascender: 1854,
      descender: -434,
      lineHeight: 1.15,
    },
    private: true,
  },
  [FONT_FAMILY.Assistant]: {
    metrics: {
      unitsPerEm: 2048,
      ascender: 1021,
      descender: -287,
      lineHeight: 1.25,
    },
    private: true,
  },
  [FONT_FAMILY.Inter]: {
    metrics: {
      unitsPerEm: 2048,
      ascender: 1984,
      descender: -494,
      lineHeight: 1.21,
    },
    category: "sans",
  },
  [FONT_FAMILY.Roboto]: {
    metrics: {
      unitsPerEm: 2048,
      ascender: 1900,
      descender: -500,
      lineHeight: 1.17,
    },
    category: "sans",
  },
  [FONT_FAMILY["Open Sans"]]: {
    metrics: {
      unitsPerEm: 2048,
      ascender: 2189,
      descender: -600,
      lineHeight: 1.36,
    },
    category: "sans",
  },
  [FONT_FAMILY.Montserrat]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 968,
      descender: -251,
      lineHeight: 1.22,
    },
    category: "sans",
  },
  [FONT_FAMILY.Poppins]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 1050,
      descender: -350,
      lineHeight: 1.4,
    },
    category: "sans",
  },
  [FONT_FAMILY["Work Sans"]]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 930,
      descender: -243,
      lineHeight: 1.17,
    },
    category: "sans",
  },
  [FONT_FAMILY.Merriweather]: {
    metrics: {
      unitsPerEm: 2000,
      ascender: 1968,
      descender: -546,
      lineHeight: 1.26,
    },
    category: "serif",
  },
  [FONT_FAMILY["Playfair Display"]]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 1082,
      descender: -251,
      lineHeight: 1.33,
    },
    category: "serif",
  },
  [FONT_FAMILY.Lora]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 1006,
      descender: -274,
      lineHeight: 1.28,
    },
    category: "serif",
  },
  [FONT_FAMILY["Libre Baskerville"]]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 970,
      descender: -270,
      lineHeight: 1.24,
    },
    category: "serif",
  },
  [FONT_FAMILY["JetBrains Mono"]]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 1020,
      descender: -300,
      lineHeight: 1.32,
    },
    category: "mono",
  },
  [FONT_FAMILY["Space Mono"]]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 1120,
      descender: -361,
      lineHeight: 1.48,
    },
    category: "mono",
  },
  [FONT_FAMILY["Roboto Mono"]]: {
    metrics: {
      unitsPerEm: 2048,
      ascender: 2146,
      descender: -555,
      lineHeight: 1.32,
    },
    category: "mono",
  },
  [FONT_FAMILY.Bangers]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 883,
      descender: -181,
      lineHeight: 1.15,
    },
    category: "display",
  },
  [FONT_FAMILY["Luckiest Guy"]]: {
    metrics: {
      unitsPerEm: 2048,
      ascender: 1440,
      descender: -608,
      lineHeight: 1.15,
    },
    category: "display",
  },
  [FONT_FAMILY["Bebas Neue"]]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 900,
      descender: -300,
      lineHeight: 1.2,
    },
    category: "display",
  },
  [FONT_FAMILY.Fredoka]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 974,
      descender: -236,
      lineHeight: 1.21,
    },
    category: "display",
  },
  [FONT_FAMILY.Righteous]: {
    metrics: {
      unitsPerEm: 2048,
      ascender: 2017,
      descender: -526,
      lineHeight: 1.24,
    },
    category: "display",
  },
  [FONT_FAMILY.Anton]: {
    metrics: {
      unitsPerEm: 2048,
      ascender: 2409,
      descender: -674,
      lineHeight: 1.51,
    },
    category: "display",
  },
  [FONT_FAMILY.Caveat]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 960,
      descender: -300,
      lineHeight: 1.26,
    },
    category: "handwriting",
  },
  [FONT_FAMILY.Kalam]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 1063,
      descender: -531,
      lineHeight: 1.59,
    },
    category: "handwriting",
  },
  [FONT_FAMILY["Patrick Hand"]]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 1042,
      descender: -312,
      lineHeight: 1.35,
    },
    category: "handwriting",
  },
  [FONT_FAMILY["Indie Flower"]]: {
    metrics: {
      unitsPerEm: 1024,
      ascender: 994,
      descender: -500,
      lineHeight: 1.46,
    },
    category: "handwriting",
  },
  [FONT_FAMILY["Shadows Into Light"]]: {
    metrics: {
      unitsPerEm: 1024,
      ascender: 1203,
      descender: -442,
      lineHeight: 1.61,
    },
    category: "handwriting",
  },
  [FONT_FAMILY["Gloria Hallelujah"]]: {
    metrics: {
      unitsPerEm: 1024,
      ascender: 1439,
      descender: -591,
      lineHeight: 1.98,
    },
    category: "handwriting",
  },
  [FONT_FAMILY["Architects Daughter"]]: {
    metrics: {
      unitsPerEm: 1024,
      ascender: 1010,
      descender: -413,
      lineHeight: 1.39,
    },
    category: "handwriting",
  },
  [FONT_FAMILY["Rock Salt"]]: {
    metrics: {
      unitsPerEm: 1024,
      ascender: 1623,
      descender: -788,
      lineHeight: 2.2,
    },
    category: "handwriting",
  },
  [FONT_FAMILY["Permanent Marker"]]: {
    metrics: {
      unitsPerEm: 1024,
      ascender: 1136,
      descender: -325,
      lineHeight: 1.43,
    },
    category: "handwriting",
  },
  [FONT_FAMILY_FALLBACKS.Xiaolai]: {
    metrics: {
      unitsPerEm: 1000,
      ascender: 880,
      descender: -144,
      lineHeight: 1.25,
    },
    fallback: true,
  },
  [FONT_FAMILY_FALLBACKS["Segoe UI Emoji"]]: {
    metrics: {
      // reusing Excalifont metrics
      unitsPerEm: 1000,
      ascender: 886,
      descender: -374,
      lineHeight: 1.25,
    },
    local: true,
    fallback: true,
  },
};

/** Unicode ranges defined by google fonts */
export const GOOGLE_FONTS_RANGES = {
  LATIN:
    "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
  LATIN_EXT:
    "U+0100-02AF, U+0304, U+0308, U+0329, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF",
  CYRILIC_EXT:
    "U+0460-052F, U+1C80-1C88, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F",
  CYRILIC: "U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116",
  VIETNAMESE:
    "U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1EA0-1EF9, U+20AB",
};

/** local protocol to skip the local font from registering or inlining */
export const LOCAL_FONT_PROTOCOL = "local:";

/**
 * Calculates vertical offset for a text with alphabetic baseline.
 */
export const getVerticalOffset = (
  fontFamily: ExcalidrawTextElement["fontFamily"],
  fontSize: ExcalidrawTextElement["fontSize"],
  lineHeightPx: number,
) => {
  const { unitsPerEm, ascender, descender } =
    FONT_METADATA[fontFamily]?.metrics ||
    FONT_METADATA[FONT_FAMILY.Excalifont].metrics;

  const fontSizeEm = fontSize / unitsPerEm;
  const lineGap =
    (lineHeightPx - fontSizeEm * ascender + fontSizeEm * descender) / 2;

  const verticalOffset = fontSizeEm * ascender + lineGap;
  return verticalOffset;
};

/**
 * Gets line height for a selected family.
 */
export const getLineHeight = (fontFamily: FontFamilyValues) => {
  const { lineHeight } =
    FONT_METADATA[fontFamily]?.metrics ||
    FONT_METADATA[FONT_FAMILY.Excalifont].metrics;

  return lineHeight as ExcalidrawTextElement["lineHeight"];
};
